import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { Skill, SkillCategory, SKILL_CATEGORIES } from '../../types';
import { toNumberOrNull } from '../../utils/format';
import ConfirmDialog from '../common/ConfirmDialog';

interface Props {
  items: Skill[];
  onCreate: (data: Partial<Skill>) => Promise<void>;
  onUpdate: (id: Skill['id'], data: Partial<Skill>) => Promise<void>;
  onDelete: (id: Skill['id']) => Promise<void>;
}

interface FormState {
  name: string;
  category: SkillCategory;
  years: string;
}

const EMPTY: FormState = { name: '', category: 'technical', years: '' };

const categoryLabel = (c: string) => SKILL_CATEGORIES.find((s) => s.value === c)?.label ?? c;

const SkillsSection: React.FC<Props> = ({ items, onCreate, onUpdate, onDelete }) => {
  const [editing, setEditing] = useState<{ id: Skill['id'] | null; form: FormState } | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Skill | null>(null);
  const [deleting, setDeleting] = useState(false);

  const grouped = useMemo(() => {
    const map = new Map<string, Skill[]>();
    SKILL_CATEGORIES.forEach((c) => map.set(c.value, []));
    items.forEach((s) => {
      if (!map.has(s.category)) map.set(s.category, []);
      map.get(s.category)!.push(s);
    });
    return Array.from(map.entries()).filter(([, list]) => list.length > 0);
  }, [items]);

  const canSave = Boolean(editing?.form.name.trim()) && !saving;

  const handleSave = async () => {
    if (!editing || !canSave) return;
    setSaving(true);
    try {
      const payload: Partial<Skill> = {
        name: editing.form.name.trim(),
        category: editing.form.category,
        years: toNumberOrNull(editing.form.years),
      };
      if (editing.id === null) {
        await onCreate({ ...payload, ordering: items.length });
      } else {
        await onUpdate(editing.id, payload);
      }
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await onDelete(pendingDelete.id);
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          Skills
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setEditing({ id: null, form: EMPTY })}
        >
          Add skill
        </Button>
      </Stack>

      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No skills added yet. Click a chip to edit it; use the x to remove it.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {grouped.map(([category, list]) => (
            <Box key={category}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {categoryLabel(category)}
              </Typography>
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                {list.map((skill) => (
                  <Chip
                    key={skill.id}
                    label={
                      skill.years !== null && skill.years !== undefined
                        ? `${skill.name} (${skill.years} yr${skill.years === 1 ? '' : 's'})`
                        : skill.name
                    }
                    onClick={() =>
                      setEditing({
                        id: skill.id,
                        form: {
                          name: skill.name,
                          category: skill.category,
                          years: skill.years === null || skill.years === undefined ? '' : String(skill.years),
                        },
                      })
                    }
                    onDelete={() => setPendingDelete(skill)}
                    variant="outlined"
                    color="primary"
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      <Dialog open={Boolean(editing)} onClose={() => !saving && setEditing(null)} fullWidth maxWidth="xs">
        <DialogTitle>{editing?.id === null ? 'Add skill' : 'Edit skill'}</DialogTitle>
        <DialogContent>
          {editing && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12}>
                <TextField
                  label="Skill"
                  value={editing.form.name}
                  onChange={(e) => setEditing((s) => (s ? { ...s, form: { ...s.form, name: e.target.value } } : s))}
                  fullWidth
                  required
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSave();
                    }
                  }}
                />
              </Grid>
              <Grid item xs={7}>
                <TextField
                  select
                  label="Category"
                  value={editing.form.category}
                  onChange={(e) =>
                    setEditing((s) =>
                      s ? { ...s, form: { ...s.form, category: e.target.value as SkillCategory } } : s
                    )
                  }
                  fullWidth
                >
                  {SKILL_CATEGORIES.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={5}>
                <TextField
                  label="Years"
                  type="number"
                  inputProps={{ min: 0, max: 60, step: 1 }}
                  value={editing.form.years}
                  onChange={(e) => setEditing((s) => (s ? { ...s, form: { ...s.form, years: e.target.value } } : s))}
                  fullWidth
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!canSave}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Remove this skill?"
        message={pendingDelete ? `"${pendingDelete.name}" will be removed from your profile.` : undefined}
        confirmLabel="Remove"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Box>
  );
};

export default SkillsSection;
